/*
Copyright 2026 Dexnet / iON.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

/*
 * iON (decision 83, Figma 08·07): "In the call · 6" — who is in the call
 * and their state (speaking, microphone off, camera off, connecting), and
 * "Invite to call", which the app answers (chat.ion.add_people).
 */

import { type FC } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@vector-im/compound-web";
import { UserAddIcon } from "@vector-im/compound-design-tokens/assets/web/icons";

import { Modal } from "../Modal";
import { Avatar, Size } from "../Avatar";
import { useBehavior } from "../useBehavior";
import { constant } from "../state/Behavior";
import { type UserMediaViewModel } from "../state/media/UserMediaViewModel";
import { pick } from "./callErrorTexts";
import { CALL_SCREEN_TEXTS as T } from "./callScreen";
import { useIonHostActions } from "./hostActions";
import styles from "./IonParticipantsSheet.module.css";

type State = "connecting" | "speaking" | "micOff" | "cameraOff" | null;

/** The one state a row shows, the most telling first. */
export function participantState(p: {
  connected: boolean;
  speaking: boolean;
  audioEnabled: boolean;
  videoEnabled: boolean;
}): State {
  if (!p.connected) return "connecting";
  if (p.speaking) return "speaking";
  if (!p.audioEnabled) return "micOff";
  if (!p.videoEnabled) return "cameraOff";
  return null;
}

const Row: FC<{ vm: UserMediaViewModel; language: string }> = ({
  vm,
  language,
}) => {
  const name = useBehavior(vm.displayName$);
  const avatar = useBehavior(vm.mxcAvatarUrl$);
  const state = participantState({
    // Our own media is always there; others' until LiveKit has them
    connected: !useBehavior(vm.local ? NOT_WAITING$ : vm.waitingForMedia$),
    speaking: useBehavior(vm.speaking$),
    audioEnabled: useBehavior(vm.audioEnabled$),
    videoEnabled: useBehavior(vm.videoEnabled$),
  });
  return (
    <li className={styles.row} data-testid="ion-participant">
      <Avatar id={vm.userId} name={name} src={avatar} size={Size.MD} />
      <div className={styles.text}>
        <span className={styles.name}>{name}</span>
        {state && (
          <span
            className={state === "speaking" ? styles.speaking : styles.state}
          >
            {pick(T[state], language)}
          </span>
        )}
      </div>
    </li>
  );
};

interface Props {
  open: boolean;
  onDismiss: () => void;
  participants: UserMediaViewModel[];
  roomId: string;
}

export const IonParticipantsSheet: FC<Props> = ({
  open,
  onDismiss,
  participants,
  roomId,
}) => {
  const { i18n } = useTranslation();
  const l = i18n.language;
  const host = useIonHostActions();
  return (
    <Modal
      title={`${pick(T.inCall, l)} · ${participants.length}`}
      open={open}
      onDismiss={onDismiss}
    >
      <ul className={styles.list}>
        {participants.map((vm) => (
          <Row key={vm.id} vm={vm} language={l} />
        ))}
      </ul>
      {host.can("add_people") && (
        <Button
          kind="primary"
          Icon={UserAddIcon}
          className={styles.invite}
          onClick={() => {
            onDismiss();
            host.send("add_people", { room_id: roomId });
          }}
        >
          {pick(T.inviteToCall, l)}
        </Button>
      )}
    </Modal>
  );
};

const NOT_WAITING$ = constant(false);
